import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { Col, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

export const enum Types {
  INJECT_TEMPLATE = 'injectTemplate',
  CHECK_USER_EXISTS = 'checkUserExists',
  USER_CREDINTIAL = 'userCredential',
}

interface ISUMActionForm {
  action: string;
  inject_fields?: null | {
    isEmailRequired?: boolean;
    isEmailChecked?: boolean;
    isPasswordRequired?: boolean;
    isPasswordChecked?: boolean;
    isFirstNameChecked?: boolean;
    isFirstNameRequired?: boolean;
  };
}

const enum SUMFiles {
  checkUserExists = 'https://cdn.1cdn.app/application/LOUISEBOT/25042813370299_user_exist.png',
  userCredintial = 'https://cdn.1cdn.app/application/LOUISEBOT/25042813370132_login_form.png',
}

export default function SuperUserMangerActionForm({
  submitTrigger,
  onActionAdded,
  setDisabled,
}: {
  submitTrigger: boolean;
  onActionAdded: (values: any, type: Types) => void;
  setDisabled: (value: boolean) => void;
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const fields = [
    { name: t('SuperUserManager/email'), key: 'Email' },
    { name: t('SuperUserManager/password'), key: 'Password' },
    { name: t('SuperUserManager/firstName'), key: 'firstName' },
    { name: t('SuperUserManager/lastName'), key: 'lastName' },
    { name: t('SuperUserManager/birthDate'), key: 'birthDate' },
    { name: t('SuperUserManager/countryCode'), key: 'countryCode' },
  ];
  const schemaAction = Yup.object({
    action: Yup.string().required(t('SuperUserManager/actionRequired')),
    inject_fields: Yup.object().shape(
      fields.reduce((acc, field) => {
        const defaultValue = field.key == 'Email' || field.key == 'Password' ? true : false;
        acc[`is${field.key}Checked`] = Yup.boolean().when('action', (action, schema) =>
          action === Types.INJECT_TEMPLATE ? schema.required().default(defaultValue) : schema.notRequired().nullable()
        );

        acc[`is${field.key}Required`] = Yup.boolean().when('action', (action, schema) =>
          action === Types.INJECT_TEMPLATE ? schema.required().default(defaultValue) : schema.notRequired().nullable()
        );
        return acc;
      }, {})
    ),
  });

  const {
    register,
    reset,
    setValue,
    handleSubmit,
    getValues,
    formState: { errors },
    watch,
    control,
  } = useForm<ISUMActionForm>({
    defaultValues: {
      action: '',
      inject_fields: null,
    },
    resetOptions: {
      keepDefaultValues: true,
    },
    resolver: yupResolver(schemaAction) as any,
    mode: 'onSubmit',
  });

  const options = Object.values(Types);

  useEffect(() => {
    if (submitTrigger) {
      ButtonSubmit();
    }
  }, [submitTrigger]);

  const ButtonSubmit = () => {
    const values = {};
    if (getValues('action') == Types.INJECT_TEMPLATE) {
      fields.map(item => {
        if (item.key != 'Email' && item.key != 'Password') {
          values[item.key] = {
            isActive: getValues(`inject_fields.is${item.key}Checked`) || false,
            isRequired: getValues(`inject_fields.is${item.key}Required`) || false,
            type: item.key == 'birthDate' ? 'date' : 'text',
          };
        }
      });
    }

    onActionAdded(values, getValues('action') as Types);
  };

  useEffect(() => {
    if (watch(`action`)) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [watch(`action`)]);
  return (
    <form onSubmit={handleSubmit(ButtonSubmit)} className="tw-w-full">
      <div className="tw-text-zinc-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none tw-w-full tw-mt-3 tw-mb-3">
        {t('SuperUserManager/action')}
      </div>

      <Col xs={12}>
        <Form.Group>
          <Form.Select
            translate="no"
            className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
            isInvalid={!!errors.action}
            {...register(`action`)}
          >
            <option value={''}> {t('SuperUserManager/selectAction')}</option>
            {options.map(option => (
              <option key={option} value={option} className="tw-capitalize">
                {t(`SuperUserManager/${option}`)}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid" className="d-block font-poppins">
            {errors.action?.message}
          </Form.Control.Feedback>
        </Form.Group>

        {watch(`action`) == Types.INJECT_TEMPLATE && (
          <>
            <div className="tw-h-px tw-border tw-border-solid tw-border-gray-200 tw-w-full tw-mt-4"></div>
            <div className="tw-text-zinc-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none tw-mt-4">{t('SuperUserManager/templateHint')}</div>
            <div className=" tw-w-96 tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-inline-flex tw-w-full tw-mt-4  tw-pb-10">
              {fields.map(({ name, key }) => (
                <div key={key} className="tw-self-stretch tw-justify-between tw-items-center tw-inline-flex">
                  {/* Checkbox */}
                  <div className="tw-justify-start tw-items-center tw-gap-2 tw-flex">
                    <Form.Check
                      defaultChecked={key == 'Email' || key == 'Password' ? true : false}
                      disabled={key == 'Email' || key == 'Password' ? true : false}
                      type="checkbox"
                      label={name}
                      className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                      id={`inject_fields.is${key}Checked`}
                      {...register(`inject_fields.is${key}Checked`)}
                    />
                  </div>

                  {/* Switch */}
                  <Form.Group>
                    <div className="tw-flex tw-items-center tw-justify-between">
                      <div className="tw-flex tw-gap-4 tw-items-center tw-mr-2">
                        <Form.Switch
                          disabled={(key == 'Email' || key == 'Password' ? true : false) || !watch(`inject_fields.is${key}Checked`)}
                          defaultChecked={key == 'Email' || key == 'Password' ? true : false}
                          {...register(`inject_fields.is${key}Required`)}
                        />
                      </div>
                      <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{t('SuperUserManager/required')}</div>
                    </div>
                  </Form.Group>
                </div>
              ))}
            </div>
          </>
        )}

        {watch('action') == Types.CHECK_USER_EXISTS && (
          <div className="tw-w-full tw-text-center">
            <img src={SUMFiles.checkUserExists} className="tw-pt-10" width={'70%'} alt="Check user exists diagram" />
          </div>
        )}

        {watch('action') == Types.USER_CREDINTIAL && (
          <div className="tw-w-full tw-text-center">
            <img src={SUMFiles.userCredintial} className="tw-pt-10" width={'80%'} alt="User credential diagram" />
          </div>
        )}
      </Col>
    </form>
  );
}
